import { Head, useForm } from "@inertiajs/react"
import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Progress } from "@/components/ui/progress"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { InfoTooltip } from "@/components/info-tooltip"
import { AppLayout } from "@/layouts/app-layout"
import { fiCalculatorPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: "/dashboard",
  },
  {
    title: "FIRE Calculator",
    href: fiCalculatorPath(),
  },
]

interface FiCalculatorProps {
  initial_data: {
    annual_expenses: number
    portfolio_value: number
    monthly_savings: number
    current_age: number
    retirement_age: number
    expected_return: number
    inflation_rate: number
    safe_withdrawal_rate: number
  }
  scenarios: {
    name: string
    description: string
    annual_expenses: number
    safe_withdrawal_rate: number
    portfolio_needed: number
  }[]
}

interface CoastFire {
  target_age: number
  coast_number: number
  achieved: boolean
  amount_remaining: number
}

interface BaristaFire {
  part_time_income: number
  barista_number: number
  achieved: boolean
  amount_remaining: number
}

interface MonteCarlo {
  years: number
  success_probability: number
  already_fi: boolean
  percentiles: {
    p10?: number
    p50?: number
    p90?: number
  }
}

interface CalculationResult {
  fi_number: number
  years_to_fi: number
  projected_age_at_fi: number
  projected_portfolio_at_fi: number
  monthly_savings_needed: number
  savings_rate_percentage: number
  coast_fire: CoastFire
  barista_fire: BaristaFire
  monte_carlo: MonteCarlo
}

export default function FiCalculator({
  initial_data,
  scenarios,
}: FiCalculatorProps) {
  const [result, setResult] = useState<CalculationResult | null>(null)
  const [isCalculating, setIsCalculating] = useState(false)

  const { data, setData, processing } = useForm({
    annual_expenses: initial_data.annual_expenses || 60000,
    portfolio_value: initial_data.portfolio_value || 100000,
    monthly_savings: initial_data.monthly_savings || 2000,
    current_age: initial_data.current_age || 30,
    retirement_age: initial_data.retirement_age || 45,
    expected_return: initial_data.expected_return || 7.0,
    inflation_rate: initial_data.inflation_rate || 3.0,
    safe_withdrawal_rate: initial_data.safe_withdrawal_rate || 4.0,
  })

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const calculateFi = async () => {
    setIsCalculating(true)
    try {
      const response = await fetch("/fi_calculator", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content") ?? "",
        },
        body: JSON.stringify({ calculation: data }),
      })

      if (response.ok) {
        const resultData = (await response.json()) as CalculationResult
        setResult(resultData)
      }
    } catch (error) {
      console.error("Calculation error:", error)
    } finally {
      setIsCalculating(false)
    }
  }

  const loadScenario = (scenario: (typeof scenarios)[0]) => {
    setData({
      ...data,
      annual_expenses: scenario.annual_expenses,
      safe_withdrawal_rate: scenario.safe_withdrawal_rate,
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="FIRE Calculator" />

      <div className="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">FIRE Calculator</h1>
            <p className="text-muted-foreground">
              Calculate your path to financial independence
            </p>
          </div>
        </div>

        <Tabs defaultValue="calculator" className="space-y-6">
          <TabsList>
            <TabsTrigger value="calculator">Interactive Calculator</TabsTrigger>
            <TabsTrigger value="scenarios">FIRE Scenarios</TabsTrigger>
          </TabsList>

          <TabsContent value="calculator" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Input Form */}
              <Card>
                <CardHeader>
                  <CardTitle>Your Financial Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="annual_expenses">Annual Expenses</Label>
                      <Input
                        id="annual_expenses"
                        type="number"
                        value={data.annual_expenses}
                        onChange={(e) =>
                          setData(
                            "annual_expenses",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="portfolio_value">Current Portfolio</Label>
                      <Input
                        id="portfolio_value"
                        type="number"
                        value={data.portfolio_value}
                        onChange={(e) =>
                          setData(
                            "portfolio_value",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="monthly_savings">Monthly Savings</Label>
                      <Input
                        id="monthly_savings"
                        type="number"
                        value={data.monthly_savings}
                        onChange={(e) =>
                          setData(
                            "monthly_savings",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="current_age">Current Age</Label>
                      <Input
                        id="current_age"
                        type="number"
                        value={data.current_age}
                        onChange={(e) =>
                          setData("current_age", parseInt(e.target.value) || 30)
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="expected_return">
                        Expected Return (%)
                        <InfoTooltip text="The average annual growth rate you expect from your investments, before inflation. 7% is a common long-term stock market assumption." />
                      </Label>
                      <Input
                        id="expected_return"
                        type="number"
                        step="0.1"
                        value={data.expected_return}
                        onChange={(e) =>
                          setData(
                            "expected_return",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="inflation_rate">
                        Inflation Rate (%)
                        <InfoTooltip text="How much prices are expected to rise each year on average. Used to keep your projections in today's purchasing power." />
                      </Label>
                      <Input
                        id="inflation_rate"
                        type="number"
                        step="0.1"
                        value={data.inflation_rate}
                        onChange={(e) =>
                          setData(
                            "inflation_rate",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="safe_withdrawal_rate">
                        Safe Withdrawal Rate (%)
                        <InfoTooltip text="The percentage of your portfolio you plan to spend each year in retirement without running out of money. 4% is the classic 'safe' benchmark — it's also what sets your FIRE Number (expenses ÷ this rate)." />
                      </Label>
                      <Input
                        id="safe_withdrawal_rate"
                        type="number"
                        step="0.1"
                        value={data.safe_withdrawal_rate}
                        onChange={(e) =>
                          setData(
                            "safe_withdrawal_rate",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                      />
                    </div>
                  </div>

                  <Button
                    onClick={() => {
                      void calculateFi()
                    }}
                    disabled={processing || isCalculating}
                    className="w-full"
                  >
                    {isCalculating ? "Calculating..." : "Calculate FIRE Number"}
                  </Button>
                </CardContent>
              </Card>

              {/* Results */}
              <Card>
                <CardHeader>
                  <CardTitle>Your FIRE Results</CardTitle>
                </CardHeader>
                <CardContent>
                  {result ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            FIRE Number
                            <InfoTooltip text="Your target portfolio size: annual expenses ÷ safe withdrawal rate." />
                          </Label>
                          <div className="text-2xl font-bold text-green-600">
                            {formatCurrency(result.fi_number)}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Years to FI
                            <InfoTooltip text="How many years until your portfolio, growing at your expected return and fed by your monthly savings, reaches your FIRE Number." />
                          </Label>
                          <div className="text-2xl font-bold">
                            {result.years_to_fi}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Age at FI
                          </Label>
                          <div className="text-xl font-semibold">
                            {result.projected_age_at_fi}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Savings Rate
                            <InfoTooltip text="The share of your monthly savings relative to your monthly expenses. Higher savings rates get you to FI faster." />
                          </Label>
                          <div className="text-xl font-semibold">
                            {result.savings_rate_percentage}%
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          Monthly Savings Needed
                          <InfoTooltip text="How much you'd need to save each month, at your expected return, to hit your FIRE Number by your target retirement age." />
                        </Label>
                        <div className="text-lg font-semibold">
                          {formatCurrency(result.monthly_savings_needed)}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          Projected Portfolio Value
                        </Label>
                        <div className="text-lg font-semibold">
                          {formatCurrency(result.projected_portfolio_at_fi)}
                        </div>
                      </div>

                      <div className="mt-4">
                        <Label className="text-sm font-medium">
                          FI Progress
                        </Label>
                        <Progress
                          value={Math.min(
                            (data.portfolio_value / result.fi_number) * 100,
                            100,
                          )}
                          className="mt-2"
                        />
                        <p className="text-muted-foreground mt-1 text-xs">
                          {(
                            (data.portfolio_value / result.fi_number) *
                            100
                          ).toFixed(1)}
                          % of FIRE number
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <p className="text-muted-foreground">
                        Enter your details and click &quot;Calculate FIRE
                        Number&quot; to see your results
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* FIRE Variants + Monte Carlo */}
            {result && (
              <div className="grid gap-6 md:grid-cols-3">
                {/* Coast FIRE */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      Coast FIRE
                      <Badge
                        variant={
                          result.coast_fire.achieved ? "default" : "outline"
                        }
                      >
                        {result.coast_fire.achieved ? "Achieved" : "Not yet"}
                      </Badge>
                    </CardTitle>
                    <p className="text-muted-foreground text-sm">
                      Invested today, coasts to your FIRE number by age{" "}
                      {result.coast_fire.target_age} with no new contributions.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="text-2xl font-bold text-green-600">
                      {formatCurrency(result.coast_fire.coast_number)}
                    </div>
                    {!result.coast_fire.achieved && (
                      <p className="text-muted-foreground text-sm">
                        {formatCurrency(result.coast_fire.amount_remaining)} to
                        go
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Barista FIRE */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      Barista FIRE
                      <Badge
                        variant={
                          result.barista_fire.achieved ? "default" : "outline"
                        }
                      >
                        {result.barista_fire.achieved ? "Achieved" : "Not yet"}
                      </Badge>
                    </CardTitle>
                    <p className="text-muted-foreground text-sm">
                      Nest egg needed if part-time work adds{" "}
                      {formatCurrency(result.barista_fire.part_time_income)}/yr.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="text-2xl font-bold text-green-600">
                      {formatCurrency(result.barista_fire.barista_number)}
                    </div>
                    {!result.barista_fire.achieved && (
                      <p className="text-muted-foreground text-sm">
                        {formatCurrency(result.barista_fire.amount_remaining)}{" "}
                        to go
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Monte Carlo */}
                <Card>
                  <CardHeader>
                    <CardTitle>Monte Carlo Success</CardTitle>
                    <p className="text-muted-foreground text-sm">
                      Odds of hitting your FIRE number across 1,000 simulated
                      market paths.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-2xl font-bold">
                      {result.monte_carlo.success_probability}%
                    </div>
                    <Progress
                      value={result.monte_carlo.success_probability}
                      className="mt-1"
                    />
                    {result.monte_carlo.already_fi ? (
                      <p className="text-muted-foreground text-xs">
                        You&apos;re already at or past your FIRE number.
                      </p>
                    ) : (
                      <div className="text-muted-foreground text-xs">
                        Range in {result.monte_carlo.years} yrs:{" "}
                        {formatCurrency(
                          result.monte_carlo.percentiles.p10 ?? 0,
                        )}{" "}
                        –{" "}
                        {formatCurrency(
                          result.monte_carlo.percentiles.p90 ?? 0,
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="scenarios" className="space-y-6">
            <div className="grid gap-4 md:grid-cols-3">
              {scenarios.map((scenario) => (
                <Card
                  key={scenario.name}
                  className="cursor-pointer transition-shadow hover:shadow-md"
                >
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      {scenario.name}
                      <Badge variant="outline">
                        {scenario.safe_withdrawal_rate}% SWR
                      </Badge>
                    </CardTitle>
                    <p className="text-muted-foreground text-sm">
                      {scenario.description}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm">Annual Expenses</Label>
                        <div className="text-lg font-semibold">
                          {formatCurrency(scenario.annual_expenses)}
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm">Portfolio Needed</Label>
                        <div className="text-xl font-bold text-green-600">
                          {formatCurrency(scenario.portfolio_needed)}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadScenario(scenario)}
                        className="w-full"
                      >
                        Use This Scenario
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  )
}
